import { Box, Typography } from '@mui/material';

import { ICurrentMonthStats } from 'src/types';
import { fCurrency } from 'src/utils/format-number';
import { toFiniteNumber } from 'src/utils/strict-number';
import { useLocales } from 'src/locales';

import DeltaCaption from './delta-caption';
import { CUSTOMERS_LABEL_KEY, formatSpan, formatSpanComparison, servedPeriodOf } from './period';

type Props = {
  currentMonthData: ICurrentMonthStats;
  /** Ties the panel to the toggle that controls it, via `aria-controls`. */
  id?: string;
  /** Id of the visible heading that names this panel. */
  labelledBy?: string;
};

/**
 * NAT-2262. Two different absences reach these cards and neither may be shown as a zero:
 *
 * - the backend sends a percentage of `null` when there is no revenue to divide by, because a
 *   percentage of nothing is undefined;
 * - during a deploy skew — this page live against a backend that has not shipped BE #1869 yet,
 *   or after a rollback — the amounts are absent entirely.
 *
 * A margin of €0 and a margin we cannot compute are very different claims, and defaulting the
 * second to the first would state something false on a page about money. Both read as a dash.
 */
const NOT_AVAILABLE = '—';

/**
 * NAT-2545 — every figure goes through the strict parser, not just the margins. The counts and
 * the revenue used to read `?? 0`, so a payload missing them — deploy skew, a partial response —
 * printed "0 ordini" and "€ 0,00" as if nothing had been sold. Unknown is a dash, like the
 * margins always were.
 */
const formatAmount = (value: unknown, lang: string) => {
  const amount = toFiniteNumber(value);
  return amount === null ? NOT_AVAILABLE : fCurrency(amount.toFixed(2), lang);
};

const formatCount = (value: unknown, lang: string) => {
  const count = toFiniteNumber(value);
  return count === null
    ? NOT_AVAILABLE
    : new Intl.NumberFormat(lang === 'en' ? 'en-US' : 'it-IT', {
        maximumFractionDigits: 0,
      }).format(count);
};

const formatMarginPercentage = (value: unknown, lang: string) => {
  const percentage = toFiniteNumber(value);
  return percentage === null
    ? NOT_AVAILABLE
    : `${new Intl.NumberFormat(lang === 'en' ? 'en-US' : 'it-IT', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(percentage)}%`;
};

export default function MonthlyStatsCards({ currentMonthData, id, labelledBy }: Props) {
  const { t, currentLang } = useLocales('operativaDashboard');

  // NAT-2334 — the span the PAYLOAD covers, not the one the toggle is on. They differ for as
  // long as a request is in flight, and permanently against a backend that predates the toggle
  // and answers every request with a month.
  const period = servedPeriodOf(currentMonthData);
  const span = formatSpan(currentMonthData.window, currentLang.value);

  // NAT-2481 — one comparison caption, shared by every card that shows a delta, so it is
  // computed once rather than re-derived (and potentially re-worded) eight times. Falls back
  // to the plain "vs anno scorso" caption when the backend has not sent both spans' day counts.
  const deltaCaption =
    formatSpanComparison(
      currentMonthData.span,
      currentMonthData.previousYearSpan,
      currentLang.value,
      t,
    ) ?? t('vs_anno_scorso');

  const cards = [
    {
      key: 'ordini_ricevuti',
      delta: currentMonthData.deltaPercentage?.ordersReceived,
      label: t('ordini_ricevuti'),
      value: formatCount(currentMonthData.ordersReceived, currentLang.value),
    },
    {
      key: 'ordini_spediti',
      delta: currentMonthData.deltaPercentage?.ordersShipped,
      label: t('ordini_spediti'),
      value: formatCount(currentMonthData.ordersShipped, currentLang.value),
    },
    {
      key: 'bottiglie',
      delta: currentMonthData.deltaPercentage?.totalBottlesSold,
      label: t('bottiglie'),
      value: formatCount(currentMonthData.totalBottlesSold, currentLang.value),
    },
    {
      key: 'ricavi',
      delta: currentMonthData.deltaPercentage?.totalRevenue,
      label: t('ricavi'),
      value: formatAmount(currentMonthData.totalRevenue, currentLang.value),
    },
    // The two margins that are honest for a month in progress. Neither deducts logistics, which
    // is imported in arrears — the fully-loaded figure lives on a closed-month view in
    // natives-bi, because a card that reads high without saying so is worse than no card.
    {
      key: 'margine',
      delta: currentMonthData.deltaPercentage?.grossMargin,
      label: t('margine'),
      value: formatAmount(currentMonthData.grossMargin, currentLang.value),
      secondary: formatMarginPercentage(currentMonthData.grossMarginPercentage, currentLang.value),
      hint: t('margine_hint'),
    },
    {
      key: 'margine_post_provvigioni',
      delta: currentMonthData.deltaPercentage?.marginAfterCommission,
      label: t('margine_post_provvigioni'),
      value: formatAmount(currentMonthData.marginAfterCommission, currentLang.value),
      secondary: formatMarginPercentage(
        currentMonthData.marginAfterCommissionPercentage,
        currentLang.value,
      ),
      hint: t('margine_post_provvigioni_hint'),
    },
    {
      key: 'nuovi_clienti',
      delta: currentMonthData.deltaPercentage?.newCustomers,
      label: t('nuovi_clienti'),
      value: formatCount(currentMonthData.newCustomers, currentLang.value),
    },
    {
      key: 'clienti_mensili',
      delta: currentMonthData.deltaPercentage?.monthlyCustomers,
      // The only card whose label names its own span: this counts the distinct customers who
      // ordered in the window, so "Clienti mensili" beside a year's figure would misstate it.
      // The other seven read the same either way. Named off the SERVED period, via the shared
      // map, so twelve months never falls through to "mensili".
      label: t(CUSTOMERS_LABEL_KEY[period]),
      value: formatCount(currentMonthData.monthlyCustomers, currentLang.value),
    },
  ];

  return (
    <Box
      id={id}
      role="region"
      // Points at the heading already on screen rather than repeating its text, which would
      // show the landmark list a label the user has just read.
      aria-labelledby={labelledBy}
      sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}
    >
      {/* The panel's real label, and the only thing that names the span for six of these eight
          cards — "Ricavi" and "Margine" read the same either way. The toggle states what was
          ASKED for, which is not always what was served; this states what WAS. */}
      {span && (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {span}
        </Typography>
      )}

      <Box
        sx={{
          display: 'grid',
          // No `sm` step on purpose: 8 cards divide evenly by 2 and 4 but not 3, and three
          // columns at 600px are narrower than two at 414px — the breakpoint whose job is to
          // give more room took it away, wrapping one margin label and misaligning the pair.
          gridTemplateColumns: {
            xs: 'repeat(2, 1fr)',
            md: 'repeat(4, 1fr)',
          },
          gap: 1.5,
        }}
      >
        {cards.map((card) => (
          <Box
            key={card.key}
            sx={{
              backgroundColor: 'background.neutral',
              borderRadius: 1,
              p: 3,
            }}
          >
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {card.label}
            </Typography>
            <Typography
              variant="h4"
              sx={{ fontWeight: 'fontWeightMedium' }}
              // A bare em-dash is not announced at default punctuation levels.
              aria-label={card.value === NOT_AVAILABLE ? t('non_disponibile') : undefined}
            >
              {card.value}
            </Typography>
            {card.secondary || card.hint ? (
              <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                {/* Guarded independently: a card with a hint and no percentage would otherwise
                  drop the hint silently. */}
                {card.secondary}
                {card.secondary && card.hint ? ' · ' : null}
                {card.hint}
              </Typography>
            ) : null}
            {/* Its own row rather than appended to the caption above: only the two margin cards
              carry a caption, so sharing it would put the comparison on a different line on
              six cards out of eight. */}
            {/*
              NAT-2437 — year-over-year only. A month-over-month caption shipped here briefly
              and was removed: for wine it reports seasonality, not momentum. December against
              November, or August against July, swings for reasons that say nothing about
              performance, and year-over-year already isolates exactly that.
            */}
            <DeltaCaption value={card.delta} caption={deltaCaption} lang={currentLang.value} />
          </Box>
        ))}
      </Box>
    </Box>
  );
}
